import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/apply`;
const DEFAULT_CONFIRM = "Thank you for your interest. We've created your Top-Recruit Candidate account so you can follow your progress, complete next steps and communicate with the recruiting team. Check your email for your secure sign-in link.";

export default function PublicApply() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [ans, setAns] = useState({});
  const [ref, setRef] = useState(sp.get("ref") || "");
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [startedSent, setStartedSent] = useState(false);
  const linkRef = sp.get("ref") || "";
  useEffect(() => {
    axios.get(`${API}/${slug}`).then((r) => { setForm(r.data); if (linkRef) { track(slug, linkRef, "opened"); } }).catch(() => setErr("This application isn't available."));
  }, [slug, linkRef]);
  const set = (id, v) => {
    setAns((a) => ({ ...a, [id]: v }));
    if (linkRef && !startedSent) { setStartedSent(true); track(slug, linkRef, "started"); }
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/${slug}`, { answers: ans, ref, website: hp }); setDone(data.message || DEFAULT_CONFIRM); window.scrollTo(0, 0); }
    catch (x) { setErr(x?.response?.data?.detail || "Something went wrong. Please try again."); } finally { setBusy(false); }
  };
  if (err && !form) return <Shell><p className="text-slate-600" data-testid="apply-unavailable">{err}</p></Shell>;
  if (!form) return <Shell><p className="text-slate-400">Loading…</p></Shell>;
  if (done) return (
    <Shell org={form}><div className="text-center space-y-3 py-8" data-testid="apply-confirmation">
      <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" /><h1 className="text-2xl font-bold text-slate-900">Application Received</h1>
      <p className="text-slate-600 max-w-md mx-auto">{done}</p></div></Shell>);
  return (
    <Shell org={form}>
      <h1 className="text-3xl font-bold text-slate-900" data-testid="apply-title">{form.title}</h1>
      {form.intro && <p className="mt-2 text-slate-600 whitespace-pre-line">{form.intro}</p>}
      <form onSubmit={submit} className="mt-6 grid grid-cols-2 gap-4" data-testid="apply-form">
        {form.fields.map((f) => <Field key={f.id} f={f} v={ans[f.id]} set={(v) => set(f.id, v)} slug={slug} />)}
        {form.manual_referral_code && <label className="col-span-2 text-sm text-slate-700">Referral code (optional)<Input value={ref} onChange={(e) => setRef(e.target.value)} className="mt-1" data-testid="apply-ref" /></label>}
        <input tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} name="website" className="hidden" aria-hidden="true" />
        {err && <p className="col-span-2 text-sm text-rose-600" data-testid="apply-error">{err}</p>}
        <div className="col-span-2"><Button type="submit" disabled={busy} className="bg-[#006090] hover:bg-[#004E75] w-full sm:w-auto" data-testid="apply-submit">{busy ? "Submitting…" : "Submit Application"}</Button></div>
      </form>
    </Shell>
  );
}

const visitorId = () => {
  try {
    let v = localStorage.getItem("tr_apply_visitor");
    if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`); localStorage.setItem("tr_apply_visitor", v); }
    return v;
  } catch { return null; }
};
const track = (slug, ref, event) => {
  const visitor = visitorId();
  if (visitor) { axios.post(`${API}/${slug}/event`, { ref, event, visitor }).catch(() => {}); }
};

const Shell = ({ org, children }) => (
  <div className="min-h-screen bg-slate-50 py-8 px-4">
    <div className="mx-auto max-w-2xl rounded-xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
      {org?.logo_url ? <img src={org.logo_url} alt="" className="h-10 mb-4" /> : org?.organization_name ? <p className="text-sm font-semibold text-[#006090] mb-4">{org.organization_name}</p> : null}
      {children}
    </div>
  </div>
);

function ResumeInput({ slug, v, set, tid }) {
  const [pct, setPct] = useState(null);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setErr("File is larger than 10 MB"); return; }
    setErr(""); setPct(0);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/${slug}/upload`, fd, { onUploadProgress: (p) => setPct(Math.round((p.loaded / (p.total || 1)) * 100)) });
      set({ upload_id: data.upload_id, token: data.token, name: data.name, size: data.size });
    } catch (x) { setErr(x?.response?.data?.detail || "Upload failed. Please try again."); } finally { setPct(null); }
  };
  if (v?.upload_id) return (
    <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid={`${tid}-done`}>
      <span className="truncate min-w-0 text-emerald-900">{v.name}</span><span className="shrink-0 text-xs text-emerald-800">{Math.max(1, Math.round(v.size / 1024))} KB</span>
      <button type="button" onClick={() => set(null)} className="shrink-0 text-xs text-slate-600 underline" data-testid={`${tid}-remove`}>Remove</button>
    </div>);
  return (
    <div className="mt-1">
      <input type="file" accept=".pdf,.doc,.docx,.rtf,.txt" onChange={pick} disabled={pct !== null} className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2" data-testid={tid} />
      {pct !== null && <div className="mt-1 h-1.5 rounded bg-slate-100"><div className="h-1.5 rounded bg-[#006090] transition-[width]" style={{ width: `${pct}%` }} data-testid={`${tid}-progress`} /></div>}
      {err && <p className="text-xs text-rose-600 mt-1" data-testid={`${tid}-error`}>{err}</p>}
    </div>);
}

function Field({ f, v, set, slug }) {
  const full = f.width !== "half" ? "col-span-2" : "col-span-2 sm:col-span-1";
  if (f.field_type === "section") return <h2 className="col-span-2 mt-4 text-lg font-semibold text-slate-800 border-b border-slate-200 pb-1">{f.label}</h2>;
  if (f.field_type === "display") return <p className="col-span-2 text-sm text-slate-600">{f.label}</p>;
  const label = <span className="text-sm font-medium text-slate-700">{f.label}{f.required && <span className="text-rose-600"> *</span>}</span>;
  const tid = `apply-field-${f.mapping || f.id}`;
  let input;
  if (["dropdown", "radio"].includes(f.field_type)) input = <select value={v || ""} onChange={(e) => set(e.target.value)} required={f.required} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid={tid}><option value="">Select…</option>{f.choices.map((c) => <option key={c}>{c}</option>)}</select>;
  else if (f.field_type === "yes_no") input = <select value={v || ""} onChange={(e) => set(e.target.value)} required={f.required} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid={tid}><option value="">Select…</option><option>Yes</option><option>No</option></select>;
  else if (["checkbox", "multi_select"].includes(f.field_type)) input = <div className="mt-1 space-y-1" data-testid={tid}>{f.choices.map((c) => (
    <label key={c} className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-1" checked={(v || []).includes(c)} onChange={(e) => set(e.target.checked ? [...(v || []), c] : (v || []).filter((x) => x !== c))} />{c}</label>))}</div>;
  else if (f.field_type === "file_upload") input = <ResumeInput slug={slug} v={v} set={set} tid={tid} />;
  else if (f.field_type === "long_text") input = <textarea value={v || ""} onChange={(e) => set(e.target.value)} required={f.required} rows={3} className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm" data-testid={tid} />;
  else input = <Input type={{ email: "email", phone: "tel", number: "number", url: "url" }[f.field_type] || "text"} value={v || ""} onChange={(e) => set(e.target.value)} required={f.required}
    pattern={f.validation || undefined} maxLength={f.mapping === "ssn_last4" ? 4 : undefined} className="mt-1" data-testid={tid} />;
  return <label className={full}>{label}{input}{f.help && <span className="block text-xs text-slate-500 mt-0.5">{f.help}</span>}</label>;
}
